<template>
  <div>
    <v-row align="center" class="mb-3">
      <v-col>
        <div class="text-h6">Histórico de Atualizações em Massa</div>
        <div class="text-caption grey--text">Acompanhe os processamentos enviados via XLSX</div>
      </v-col>
      <v-col class="text-right">
        <v-btn
          depressed
          class="mr-2"
          :to="{ name: 'productIndex' }"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Produtos
        </v-btn>
        <v-btn
          depressed
          color="primary"
          class="mr-2"
          :to="{ name: 'ProductMassUpdateIndex' }"
        >
          <v-icon left>mdi-table-arrow-up</v-icon>
          Nova atualização
        </v-btn>
        <v-btn icon :loading="loading" @click="select()">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <div class="text-caption grey--text mb-3 text-right">
      <v-icon small color="orange">mdi-information-outline</v-icon>
      Os totais refletem jobs (chunks de até 25 produtos). Ex: 53 produtos = 3 jobs.
    </div>

    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="job_batches.data"
      :items-per-page="-1"
      hide-default-footer
      disable-sort
      @click:row="openDetail($event)"
      style="cursor: pointer"
    >
      <template v-slot:[`item.created_at`]="{ item }">
        {{ tsFormat(item.created_at) }}
      </template>

      <template v-slot:[`item.finished_at`]="{ item }">
        <span v-if="item.finished_at">{{ tsFormat(item.finished_at) }}</span>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:[`item.total_jobs`]="{ item }">
        <span class="font-weight-medium">{{ item.total_jobs }}</span>
      </template>

      <template v-slot:[`item.processed_jobs`]="{ item }">
        <span class="success--text font-weight-medium">{{ item.processed_jobs }}</span>
      </template>

      <template v-slot:[`item.pending_jobs`]="{ item }">
        <span :class="item.pending_jobs > 0 ? 'primary--text' : 'grey--text'">
          {{ item.pending_jobs }}
        </span>
      </template>

      <template v-slot:[`item.failed_jobs`]="{ item }">
        <v-chip v-if="item.failed_jobs > 0" small color="error" text-color="white">
          {{ item.failed_jobs }}
        </v-chip>
        <span v-else class="grey--text">0</span>
      </template>

      <template v-slot:[`item.progress`]="{ item }">
        <div class="d-flex align-center" style="min-width: 140px">
          <v-progress-linear
            :value="item.progress"
            :color="statusData[item.status].color"
            height="8"
            rounded
            class="mr-2"
            style="flex: 1"
          />
          <span class="text-body-2 font-weight-medium" style="min-width: 40px">
            {{ item.progress }}%
          </span>
        </div>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip
          small
          :color="statusData[item.status].color"
          text-color="white"
        >
          <v-icon left small>{{ statusData[item.status].icon }}</v-icon>
          {{ statusData[item.status].text }}
        </v-chip>
      </template>
    </v-data-table>

    <app-pagination :data="job_batches" @click="select($event)" />

    <!-- Dialog de detalhes -->
    <v-dialog v-model="dialog" max-width="560" scrollable>
      <v-card v-if="selected">
        <v-card-text class="pa-6">
          <!-- Header -->
          <div class="d-flex align-start justify-space-between mb-5">
            <div>
              <div class="text-subtitle-1 font-weight-semibold mb-1">Detalhes do processamento</div>
              <code class="text-caption grey--text">{{ selected.id }}</code>
            </div>
            <v-btn icon small @click="dialog = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Progresso -->
          <div class="mb-5">
            <div class="d-flex justify-space-between mb-1">
              <span class="text-caption grey--text">Progresso</span>
              <span class="text-caption font-weight-medium">{{ selected.progress }}%</span>
            </div>
            <v-progress-linear
              :value="selected.progress"
              color="grey darken-2"
              background-color="grey lighten-3"
              height="4"
              rounded
            />
          </div>

          <!-- Métricas -->
          <div class="d-flex mb-5" style="gap: 8px">
            <div class="metric-box flex-grow-1">
              <div class="metric-value">{{ selected.total_jobs }}</div>
              <div class="metric-label">Total</div>
            </div>
            <div class="metric-box flex-grow-1">
              <div class="metric-value">{{ selected.processed_jobs }}</div>
              <div class="metric-label">Processados</div>
            </div>
            <div class="metric-box flex-grow-1">
              <div class="metric-value">{{ selected.pending_jobs }}</div>
              <div class="metric-label">Pendentes</div>
            </div>
            <div class="metric-box flex-grow-1" :class="selected.failed_jobs > 0 ? 'metric-box--error' : ''">
              <div class="metric-value" :class="selected.failed_jobs > 0 ? 'error--text' : ''">
                {{ selected.failed_jobs }}
              </div>
              <div class="metric-label">Falhas</div>
            </div>
          </div>

          <!-- Info -->
          <div class="info-row">
            <span class="info-label">Status</span>
            <v-chip x-small :color="statusData[selected.status].color" text-color="white">
              {{ statusData[selected.status].text }}
            </v-chip>
          </div>
          <div class="info-row">
            <span class="info-label">Iniciado em</span>
            <span class="text-body-2">{{ tsFormat(selected.created_at) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Finalizado em</span>
            <span class="text-body-2">
              {{ selected.finished_at ? tsFormat(selected.finished_at) : '—' }}
            </span>
          </div>
          <div v-if="selected.cancelled_at" class="info-row">
            <span class="info-label">Cancelado em</span>
            <span class="text-body-2 error--text">{{ tsFormat(selected.cancelled_at) }}</span>
          </div>

          <!-- Jobs falhados -->
          <template v-if="selected.failed_job_ids && selected.failed_job_ids.length > 0">
            <v-divider class="my-4" />
            <div class="text-caption grey--text mb-2">
              Jobs falhados ({{ selected.failed_job_ids.length }})
            </div>
            <div class="failed-jobs-list">
              <div
                v-for="(jobId, i) in selected.failed_job_ids"
                :key="i"
                class="failed-job-item"
              >
                <code class="text-caption">{{ jobId }}</code>
              </div>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      dialog: false,
      selected: null,
      job_batches: { data: [] },
      searchParams: { page: 1 },
      statusData: {
        running: {
          text: 'Em andamento',
          color: 'primary',
          icon: 'mdi-timer-sand',
        },
        finished: {
          text: 'Concluído',
          color: 'success',
          icon: 'mdi-check',
        },
        finished_with_failures: {
          text: 'C/ erros',
          color: 'warning',
          icon: 'mdi-alert',
        },
        cancelled: {
          text: 'Cancelado',
          color: 'error',
          icon: 'mdi-cancel',
        },
      },
      headers: [
        { text: 'Iniciado em',   value: 'created_at',    align: 'center' },
        { text: 'Finalizado em', value: 'finished_at',   align: 'center' },
        { text: 'Total',         value: 'total_jobs',    align: 'center', width: '80px' },
        { text: 'Processados',   value: 'processed_jobs', align: 'center', width: '100px' },
        { text: 'Pendentes',     value: 'pending_jobs',  align: 'center', width: '90px' },
        { text: 'Falhas',        value: 'failed_jobs',   align: 'center', width: '80px' },
        { text: 'Progresso',     value: 'progress',      align: 'center', width: '180px' },
        { text: 'Status',        value: 'status',        align: 'center', width: '130px' },
      ],
    }
  },

  created() {
    this.select()
  },

  methods: {
    select(page = 1) {
      this.searchParams.page = page
      this.loading = true
      this.$http
        .index('product/mass-update-v2', this.searchParams)
        .then((response) => {
          this.job_batches = response.job_batches
        })
        .finally(() => {
          this.loading = false
        })
    },

    openDetail(batch) {
      this.selected = batch
      this.dialog = true
    },

    tsFormat(timestamp) {
      if (!timestamp) return '—'
      return this.$format.dateTimeBr(new Date(timestamp * 1000).toISOString())
    },
  },
}
</script>

<style scoped>
.metric-box {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 12px 8px;
  text-align: center;
}
.metric-box--error {
  background: #fff5f5;
}
.metric-value {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.2;
}
.metric-label {
  font-size: 0.7rem;
  color: #9e9e9e;
  margin-top: 2px;
}
.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.info-row:last-of-type {
  border-bottom: none;
}
.info-label {
  font-size: 0.8rem;
  color: #9e9e9e;
}
.failed-jobs-list {
  max-height: 160px;
  overflow-y: auto;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 8px 12px;
}
.failed-job-item {
  padding: 3px 0;
  border-bottom: 1px solid #f0f0f0;
}
.failed-job-item:last-child {
  border-bottom: none;
}
</style>
